/* eslint-disable */
import { useState, useEffect, useCallback } from "react";
import api from "api";
import { toast } from "react-toastify";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
  Badge,
} from "reactstrap";
import { Store } from "StoreContext";

// Multi-admin team management. Brand-owners invite teammates and pick one
// of these scoped roles. Staff/readonly viewers see the same table below
// but the Invite/Remove actions stay owner-only.
//
// Each role's `details` array shows up in the role-explainer panel below
// the picker so the owner sees exactly what they're handing out before
// they send the invite. Keep these in sync with the route-level guards in
// the API (see middleware/auth.middleware.ts and the per-route
// requireBrandRole(...) calls).
const ROLE_OPTIONS = [
  {
    value: "staff",
    label: "Staff",
    summary: "Day-to-day operations across orders + rewards.",
    details: {
      can: [
        "Fulfill redemptions (mark shipped / in-person, add tracking)",
        "Print packing slips, resend brand emails",
        "Create + edit orders + batches",
        "Send test order emails",
      ],
      cannot: [
        "Edit brand profile, theme, or integrations",
        "Add or edit reward catalog products",
        "Invite or remove teammates",
      ],
    },
  },
  {
    value: "rewards_manager",
    label: "Rewards Manager",
    summary: "Fulfillment-only access for warehouse / shipping staff.",
    details: {
      can: [
        "View incoming redemptions",
        "Mark redemptions as shipped or fulfilled in person",
        "Print packing slips",
        "Resend brand notification emails",
      ],
      cannot: [
        "Edit reward catalog products (no add / edit / reorder)",
        "Touch order batches, brand profile, theme, integrations, or webhooks",
        "Invite or remove teammates",
      ],
    },
  },
  {
    value: "readonly",
    label: "Read-only",
    summary: "View everything. Touch nothing.",
    details: {
      can: [
        "Browse the dashboard, customers, redemptions, and reports",
      ],
      cannot: [
        "Make any change anywhere — pure observer access",
      ],
    },
  },
];

const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};

const TeamMembers = () => {
  const store = Store();
  const me = store?.user || {};
  const isOwner = me.role === "brand" && (me.brandRole || "owner") === "owner";

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("staff");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api("get", "/users/team")
      .then((res) => {
        if (res?.success) setMembers(res.members || []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (e) => {
    if (e) e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    const name = inviteName.trim();
    if (!name || !email) {
      toast.error("Name and email are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email.");
      return;
    }
    setInviting(true);
    try {
      const res = await api("post", "/users/team/invite", { name, email, brandRole: inviteRole });
      if (res?.success) {
        toast.success(`Invite sent to ${email}.`);
        setShowInvite(false);
        setInviteName("");
        setInviteEmail("");
        setInviteRole("staff");
        load();
      }
    } catch (_err) {
      // interceptor toasts
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId, email) => {
    if (!window.confirm(`Remove ${email}? They'll lose access immediately.`)) return;
    try {
      const res = await api("delete", `/users/team/${userId}`);
      if (res?.success) {
        toast.success("Team member removed.");
        load();
      }
    } catch (_err) {
      // interceptor toasts
    }
  };

  const renderRoleBadge = (m) => {
    // Owner-row vs invited teammates. parentBrandId === null means owner.
    const role = !m.parentBrandId ? "owner" : (m.brandRole || "staff");
    const map = {
      owner: { color: "primary", label: "Owner" },
      staff: { color: "info", label: "Staff" },
      rewards_manager: { color: "warning", label: "Rewards Mgr" },
      readonly: { color: "secondary", label: "Read-only" },
    };
    const x = map[role] || map.staff;
    return <Badge color={x.color} className="text-uppercase">{x.label}</Badge>;
  };

  const renderStatusBadge = (m) => {
    if (!m.parentBrandId) return <Badge color="success">Active</Badge>;
    if (m.acceptedAt) return <Badge color="success">Active</Badge>;
    return <Badge color="warning">Pending invite</Badge>;
  };

  return (
    <Container className="mt-4" fluid>
      <Row>
        <Col xs="12">
          <Card className="shadow">
            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
              <h3 className="mb-0">Team Members</h3>
              {isOwner && (
                <Button color="primary" size="sm" onClick={() => setShowInvite(true)}>
                  Invite team member
                </Button>
              )}
            </CardHeader>
            <CardBody>
              {!isOwner && (
                <p className="text-muted small mb-3">
                  You're viewing your team. Only the brand owner can invite or remove members.
                </p>
              )}
              {loading ? (
                <div className="text-center py-4"><Spinner size="sm" /> Loading…</div>
              ) : members.length === 0 ? (
                <p className="text-muted">No team members yet.</p>
              ) : (
                <Table responsive hover className="align-items-center">
                  <thead className="thead-light">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Invited</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => {
                      const isSelf = String(m._id) === String(me._id);
                      const isOwnerRow = !m.parentBrandId;
                      return (
                        <tr key={m._id}>
                          <td>{m.name}{isSelf ? " (you)" : ""}</td>
                          <td>{m.email || "—"}</td>
                          <td>{renderRoleBadge(m)}</td>
                          <td>{formatDate(m.createdAt)}</td>
                          <td>{renderStatusBadge(m)}</td>
                          <td className="text-right">
                            {isOwner && !isOwnerRow && !isSelf && (
                              <Button
                                color="link"
                                size="sm"
                                className="text-danger"
                                onClick={() => handleRemove(m._id, m.email)}
                              >
                                Remove
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={showInvite} toggle={() => setShowInvite(false)}>
        <ModalHeader toggle={() => setShowInvite(false)}>Invite team member</ModalHeader>
        <form onSubmit={handleInvite}>
          <ModalBody>
            <FormGroup>
              <Label>Name</Label>
              <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Jane Doe" autoFocus />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="jane@example.com" />
            </FormGroup>
            <FormGroup>
              <Label>Role</Label>
              <Input type="select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </Input>
              {(() => {
                const r = ROLE_OPTIONS.find((x) => x.value === inviteRole);
                if (!r) return null;
                return (
                  <div
                    style={{
                      marginTop: 10,
                      padding: 12,
                      background: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      fontSize: 13,
                      color: "#374151",
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#111827", marginBottom: 6 }}>
                      {r.label}
                    </div>
                    <div style={{ color: "#6B7280", marginBottom: 10 }}>{r.summary}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600, color: "#047857", fontSize: 12, marginBottom: 4 }}>
                          ✓ Can do
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, color: "#111827" }}>
                          {r.details.can.map((line) => (
                            <li key={line} style={{ fontSize: 12, marginBottom: 2 }}>{line}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: "#B91C1C", fontSize: 12, marginBottom: 4 }}>
                          ✗ Cannot
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, color: "#111827" }}>
                          {r.details.cannot.map((line) => (
                            <li key={line} style={{ fontSize: 12, marginBottom: 2 }}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={() => setShowInvite(false)} disabled={inviting}>Cancel</Button>
            <Button color="primary" type="submit" disabled={inviting}>
              {inviting ? <Spinner size="sm" /> : "Send invite"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </Container>
  );
};

export default TeamMembers;
